import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Info } from 'lucide-react'

const getSourceColor = (sourceName) => {
  const colors = {
    Admob: 'bg-blue-500 hover:bg-blue-600',
    Meta: 'bg-purple-500 hover:bg-purple-600',
    Unity: 'bg-green-500 hover:bg-green-600',
    AppLovin: 'bg-orange-500 hover:bg-orange-600',
    IronSource: 'bg-red-500 hover:bg-red-600',
    Vungle: 'bg-pink-500 hover:bg-pink-600',
  }
  return colors[sourceName] || 'bg-gray-500 hover:bg-gray-600'
}

function PamMediationGroupsTable({
  groups,
  selectedGroups,
  onSelectAll,
  onSelectGroup,
  onEdit,
  onShowInfo,
}) {
  if (!groups || groups.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        该应用未找到中介组
      </div>
    )
  }

  // 只考虑没有A/B实验的中介组
  const editableGroups = groups.filter((group) => !group.hasAbTest)
  const allSelected =
    editableGroups.length > 0 &&
    editableGroups.every((group) => selectedGroups.includes(group.id))
  const someSelected = selectedGroups.length > 0 && !allSelected

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">
              <Checkbox
                checked={allSelected}
                onCheckedChange={onSelectAll}
                aria-label="Select all"
                className={someSelected ? 'data-[state=checked]:bg-gray-400' : ''}
              />
            </TableHead>
            <TableHead className="w-[300px]">名称</TableHead>
            <TableHead className="w-[100px]">类型</TableHead>
            <TableHead>Waterfall 价格</TableHead>
            <TableHead className="w-[100px]">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => (
            <TableRow
              key={group.id}
              className={group.hasAbTest ? 'bg-blue-50' : ''}
            >
              <TableCell>
                <Checkbox
                  checked={selectedGroups.includes(group.id)}
                  onCheckedChange={() => onSelectGroup(group.id)}
                  aria-label={`Select ${group.name}`}
                  disabled={group.hasAbTest}
                />
              </TableCell>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <span>{group.name}</span>
                  {group.hasAbTest && (
                    <Badge variant="secondary" className="text-xs">
                      A/B测试
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1 break-all">
                  {group.platform && <span>{group.platform} · </span>}
                  {(group.adUnitIds || []).join(', ')}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{group.type}</Badge>
              </TableCell>
              <TableCell>
                {group.hasAbTest && group.waterfallGroups ? (
                  // A/B 分组展示
                  <div className="space-y-2">
                    {group.waterfallGroups.map((wfGroup, groupIdx) => (
                      <div key={groupIdx} className="flex items-start gap-2">
                        <div className="flex-shrink-0 min-w-[40px]">
                          <Badge
                            variant={wfGroup.group === 'A' ? 'outline' : 'default'}
                            className={
                              wfGroup.group === 'A'
                                ? 'bg-gray-100 text-gray-700 border-gray-300'
                                : 'bg-green-600 text-white'
                            }
                          >
                            {wfGroup.group}组
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-1 flex-1">
                          {wfGroup.waterfall.map((item, index) => (
                            <Badge
                              key={`${wfGroup.group}-${item.id || index}-${item.name}-${item.price}`}
                              className={`${getSourceColor(item.name)} text-white`}
                            >
                              {item.name}-${item.price}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // 常规单行展示
                  <div className="flex flex-wrap gap-1">
                    {group.waterfall.map((item, index) => (
                      <Badge
                        key={`${item.id || index}-${item.name}-${item.price}`}
                        className={`${getSourceColor(item.name)} text-white`}
                      >
                        {item.name}-${item.price}
                      </Badge>
                    ))}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {group.hasAbTest ? (
                    <div className="text-sm text-gray-500 italic">
                      实验进行中
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => onEdit(group)}
                    >
                      编辑
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onShowInfo(group)}
                    title="查看 Mapping 信息"
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default PamMediationGroupsTable
